import '../../../../pages/Admin/Admin.scss';
import './EventEditor.scss';

import { useCallback, useEffect } from 'react';

import DateAndTimeSettings from 'components/Admin/EventSettings/DateAndTimeSettings/DateAndTimeSettings';
import EventPreview from 'components/Admin/EventSettings/EventPreview/EventPreview';
import EventTitleInput from 'components/Admin/EventSettings/EventTitleInput/EventTitleInput';
import LocationSettings from 'components/Admin/EventSettings/LocationSettings/LocationSettings';
import MembershipRequired from 'components/Admin/StatusSettings/MembershipRequired';
import Paragraph from 'components/TextComps/Paragraph';
import RecordingReady from 'components/Admin/EventSettings/RecordingReady/RecordingReady';
import RecordingSettings from 'components/Admin/EventSettings/RecordingSettings/RecordingSettings';
import URLInput from 'components/Admin/EventSettings/URLInput/URLInput';
import VideoLength from 'components/Admin/EventSettings/VideoLength/VideoLength';
import VisibleSetting from 'components/Admin/StatusSettings/VisibleSetting';
import { cleanEventObject } from 'stores/Admin/Editors/TrackmanEventsEditorStore';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { useStores } from 'index';

const isNonEmpty = (value: string | undefined) => value && value.trim() !== '';

export const EventEditor = observer(() => {
  useSetPageTitle({ title: 'Edit Event' });
  const eventId = useParams().eventId;
  const es = useStores().trackmanEventsEditorStore;

  useEffect(() => {
    if (eventId) {
      es.getEvent(eventId);
    }
    return () => {
      es.eventItem = structuredClone(cleanEventObject);
    };
  }, [es, eventId]);

  useEffect(() => {
    if (es.eventItem.videoId) {
      es.isVideoIdSelected = true;
      return;
    }
    es.isVideoIdSelected = false;
  }, [es.eventItem.videoId, es]);

  const isTitleValid = useCallback(() => es.eventItem.title !== '', [es.eventItem.title]);
  const isMeetingUrlValid = useCallback(() => isNonEmpty(es.eventItem.meetingUrl), [es.eventItem.meetingUrl]);
  const isLocationValid = useCallback(() => isNonEmpty(es.eventItem.location ?? undefined), [es.eventItem.location]);
  const isStartDateValid = useCallback(() => isNonEmpty(es.eventItem.startDate), [es.eventItem.startDate]);
  const isImageValid = useCallback(
    () => isNonEmpty(es.eventItem.imageUrl) || es.eventItem.imageData !== undefined,
    [es.eventItem.imageUrl, es.eventItem.imageData]
  );
  const isRecordingReadyValid = useCallback(() => {
    if (!es.eventItem.recordingReady) return isImageValid();
    if (!es.isVideoIdSelected) return isNonEmpty(es.eventItem.videoUrl ?? undefined) && isImageValid();
    return isNonEmpty(es.eventItem.videoId ?? undefined);
  }, [es.eventItem.recordingReady, es.isVideoIdSelected, es.eventItem.videoUrl, es.eventItem.videoId, isImageValid]);

  const disableButton = useCallback(
    () =>
      !isTitleValid() ||
      !isMeetingUrlValid() ||
      (es.eventItem.locationType !== 'Online' && !isLocationValid()) ||
      !isStartDateValid() ||
      !isRecordingReadyValid(),
    [isTitleValid, isMeetingUrlValid, isLocationValid, isStartDateValid, isRecordingReadyValid, es.eventItem.locationType]
  );

  useEffect(() => {
    eventId && disableButton();
  }, [eventId, disableButton]);

  const publishEvent = useCallback(() => {
    if (eventId) {
      es.saveExistingEventAndUpdate();
    } else {
      es.saveNewEventAndUpdate();
    }
  }, [eventId, es]);

  return (
    <>
      <div className='default-page-header'>
        <h1 className='page-title'>Edit Event</h1>
      </div>
      <div className='admin-content-container edit-event'>
        <div className='edit-event-columns'>
          <div className='edit-event-column left-column'>
            <EventTitleInput />
            <URLInput />
            <LocationSettings />
            <DateAndTimeSettings />
            <MembershipRequired
              hint='Only users with Premium subscription will be able to attend this Event.'
              onChange={() => (es.eventItem.premiumEvent = !es.eventItem.premiumEvent)}
              checked={es.eventItem.premiumEvent}
            />
            <div className='admin-edit-section'>
              <h2>Settings</h2>
              <div className='checkboxes-container checkbox-with-hint'>
                <VisibleSetting dataItem={es.eventItem} />
                <Paragraph>
                  <span className='helper-txt'>When the Event ends, it will be automatically hidden.</span>
                </Paragraph>
              </div>
              <RecordingReady />
            </div>

            {es.eventItem.recordingReady ? <RecordingSettings /> : null}
            {es.eventItem.recordingReady ? <VideoLength /> : null}
            <div className='admin-edit-section'>
              <Paragraph>
                <>* required field</>
              </Paragraph>
            </div>
          </div>
          <div className='edit-event-column right-column'>
            <EventPreview />
          </div>
        </div>
        <div className='admin-footer'>
          <button className='styled-button primary' onClick={() => publishEvent()} disabled={disableButton()}>
            Publish
          </button>
        </div>
      </div>
    </>
  );
});

export default EventEditor;
